import { FC } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { format, parseISO } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { shortDate } from 'utilities';
import { ReviewPriceDialogContentProps } from './ReviewPriceDialog.props';
import { reviewPriceDialogColors as colors } from './ReviewPriceDialog.styles';

const fieldLabelSx = { color: colors.slate700, fontSize: 12, fontWeight: 600 };

const formatCurrency = (value: number | null | undefined, currencyCode = 'USD', fractionDigits = 0) =>
  isNil(value)
    ? '—'
    : Number(value).toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });

const formatNoteTime = (value?: string) => {
  if (!value) {
    return '';
  }
  try {
    return format(parseISO(value), 'MMM d, yyyy, h:mm a');
  } catch {
    return value;
  }
};

const ReviewPriceDialogContent: FC<ReviewPriceDialogContentProps> = ({
  row,
  triggers,
  formValues,
  setFormValues,
  notes,
  noteDraft,
  setNoteDraft,
  onAddNote,
  onDeleteNote,
  reviewFieldsLocked = false,
  notesLocked = false,
}) => {
  const lookingGlass = row?.lookingGlass;
  const currencyCode = lookingGlass?.cap_table_currency || 'USD';
  let priceAdjustedValue: 'yes' | 'no' | null = null;
  if (formValues.price_adjusted === true) {
    priceAdjustedValue = 'yes';
  } else if (formValues.price_adjusted === false) {
    priceAdjustedValue = 'no';
  }
  // Equity Value and Price / Share are restated in the NAV reporting currency
  // per field, and only when a rate was available -- so the label has to come
  // from converted_fields, exactly as the grid's own cells do. Labelling a
  // converted figure with the cap table currency is the failure this avoids.
  const snapshotCurrency = (field: string) =>
    (lookingGlass?.converted_fields?.includes(field) && lookingGlass?.reporting_currency) || currencyCode;

  const removeNote = (index: number) => {
    setFormValues({ ...formValues, review_notes: notes.filter((_, i) => i !== index).join('\n') });
  };

  return (
    <Box>
      <Typography sx={{ color: colors.slate500, fontSize: 12, marginBottom: 1.5 }}>{row?.companyName ?? ''}</Typography>
      {reviewFieldsLocked && (
        <Typography sx={{ color: colors.slate500, fontSize: 12, fontStyle: 'italic', marginBottom: 1.5 }}>
          This Daily NAV day is finalized. Review status is locked; notes can still be added.
        </Typography>
      )}

      <Box display="flex" flexDirection="column">
        <FormControlLabel
          control={
            <Checkbox
              id="review-price-reviewed-by-scalar"
              size="small"
              checked={formValues.reviewed_by_scalar}
              disabled={reviewFieldsLocked}
              onChange={event => setFormValues({ ...formValues, reviewed_by_scalar: event.target.checked })}
            />
          }
          label={<Typography sx={{ color: colors.slate700, fontSize: 12 }}>Reviewed by Scalar</Typography>}
        />
        <FormControlLabel
          control={
            <Checkbox
              id="review-price-discussed-with-fund"
              size="small"
              checked={formValues.discussed_with_fund}
              disabled={reviewFieldsLocked}
              onChange={event => setFormValues({ ...formValues, discussed_with_fund: event.target.checked })}
            />
          }
          label={<Typography sx={{ color: colors.slate700, fontSize: 12 }}>Discussed with Fund</Typography>}
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} marginY={1.5}>
        <ToggleButtonGroup
          id="review-price-price-adjusted"
          exclusive
          size="small"
          disabled={reviewFieldsLocked}
          value={priceAdjustedValue}
          onChange={(_event, newValue) => {
            if (newValue === 'yes') {
              setFormValues({ ...formValues, price_adjusted: true });
            } else if (newValue === 'no') {
              setFormValues({ ...formValues, price_adjusted: false });
            }
          }}
          sx={{
            '& .MuiToggleButton-root': {
              border: `1px solid ${colors.border}`,
              color: colors.slate700,
              fontSize: 11,
              lineHeight: 1.2,
              paddingX: 1.5,
              paddingY: 0.5,
              textTransform: 'none',
              '&.Mui-selected': {
                backgroundColor: colors.navy,
                color: '#fff',
                '&:hover': { backgroundColor: colors.navyHover },
              },
            },
          }}>
          <ToggleButton value="no">No</ToggleButton>
          <ToggleButton value="yes">Yes</ToggleButton>
        </ToggleButtonGroup>
        <Typography sx={fieldLabelSx}>Price adjusted</Typography>
      </Box>

      {!isEmpty(triggers) && (
        <Box marginY={1.5}>
          <Typography sx={fieldLabelSx}>Triggers</Typography>
          <Box component="ul" sx={{ marginY: 0.5, paddingLeft: 2.5 }}>
            {triggers.map(trigger => (
              <Typography key={trigger} component="li" sx={{ color: colors.slate700, fontSize: 11 }}>
                {trigger}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {lookingGlass && (
        <Table
          size="small"
          sx={{
            border: `1px solid ${colors.border}`,
            marginY: 1.5,
            '& th, & td': { border: 'none', borderTop: `1px solid ${colors.border}`, paddingX: 0.75, paddingY: 0.5 },
          }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: colors.tableHeaderBackground }} />
              <TableCell
                align="center"
                sx={{
                  backgroundColor: colors.tableHeaderBackground,
                  color: colors.slate800,
                  fontSize: 11,
                  fontWeight: 600,
                }}>
                {`${shortDate(lookingGlass.measurement_date_value)} (previous valuation)`}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ color: colors.slate800, fontSize: 11, fontWeight: 600 }}>
                Equity Value
              </TableCell>
              <TableCell align="right" sx={{ color: colors.slate800, fontSize: 11 }}>
                {formatCurrency(lookingGlass.equity_value, snapshotCurrency('equity_value'))}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell component="th" scope="row" sx={{ color: colors.slate800, fontSize: 11, fontWeight: 600 }}>
                Price/Share
              </TableCell>
              <TableCell align="right" sx={{ color: colors.slate800, fontSize: 11 }}>
                {formatCurrency(lookingGlass.common_share_value, snapshotCurrency('common_share_value'), 2)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}

      <Box marginTop={1.5}>
        <Typography sx={fieldLabelSx}>Review Notes</Typography>
        {isEmpty(notes) ? (
          <Typography sx={{ color: colors.slate500, fontSize: 11, fontStyle: 'italic', marginY: 0.5 }}>
            No notes yet.
          </Typography>
        ) : (
          <Box marginY={0.5}>
            {notes.map(note => (
              <Box
                key={note.id}
                display="flex"
                alignItems="baseline"
                justifyContent="space-between"
                gap={1}
                sx={{ borderBottom: `1px solid ${colors.border}`, paddingY: 0.5 }}>
                <Box>
                  <Typography sx={{ color: colors.slate700, fontSize: 11 }}>{note.body}</Typography>
                  <Typography sx={{ color: colors.slate500, fontSize: 10 }}>
                    {note.created_by_name || 'Unknown'} · {formatNoteTime(note.created_at)}
                  </Typography>
                </Box>
                {!notesLocked && onDeleteNote && (
                  <Button
                    size="small"
                    onClick={() => note.id != null && onDeleteNote(note.id)}
                    sx={{ color: colors.slate500, fontSize: 10, minWidth: 0, padding: 0, textTransform: 'none' }}>
                    Delete
                  </Button>
                )}
              </Box>
            ))}
          </Box>
        )}
        {!notesLocked && (
          <>
            <TextField
              id="review-price-review-notes"
              placeholder="Add a note…"
              multiline
              minRows={3}
              fullWidth
              margin="dense"
              value={noteDraft}
              onChange={event => setNoteDraft(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': { fontSize: 12 },
                '& fieldset': { borderColor: colors.border },
              }}
            />
            <Button
              id="review-price-add-note-button"
              onClick={onAddNote}
              disabled={!noteDraft.trim()}
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: '4px',
                color: colors.slate700,
                fontSize: 11,
                marginTop: 0.5,
                paddingX: 1.25,
                paddingY: 0.25,
                textTransform: 'none',
              }}>
              Add note
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
};

export default ReviewPriceDialogContent;
